import { useAuth } from "AuthProvider";
import useFetch from "hooks/useFetch";
import React, { useEffect, useRef, useState } from "react";
import { CSVLink } from "react-csv";
import {
  Badge,
  Card,
  EmptyState,
  Icon,
  PageHeader,
  Select,
  styles as ui,
  Table,
} from "stories/Admin";
import PrayerTimesSkeleton from "./PrayerTimesSkeleton/PrayerTimesSkeleton";
import styles from "./prayerTimes.module.scss";

interface PrayerTimes {
  date: string;
  fajr: string;
  fajr_endtime: string;

  duhr: string;
  asr: string;
  maghrib: string;
  isha: string;
  asr_1x_shadow: string;
  asr_2x_shadow: string;
}

const PrayerTimesPage: React.FC = () => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [prayerTimesData, setPrayerTimesData] = useState<PrayerTimes[]>([]);
  const [csvData, setCsvData] = useState<any[]>([]);
  const { execute, isLoading, error } = useFetch<PrayerTimes[]>();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // Only for display; taken from the mosque response that is already fetched.
  const [asrMethod, setAsrMethod] = useState<string | null>(null);

  const { execute: fetchMosqueData } = useFetch<any>();
  const {
    locationId,
    prayerTimeTableId,
    selectedOrgNumber,
    setLocationId,
    setPrayerTimeTableId,
    isSuperAdmin,
    mosqueName,
    selectedOrgName,
  } = useAuth();

  const getMonthName = (date: Date) => MONTHS_LONG[date.getMonth()];

  const fetchPrayerTimes = async () => {
    if (!locationId || !prayerTimeTableId) return;

    const month = (currentMonth.getMonth() + 1).toString().padStart(2, "0");
    const endpoint = `/api/v1/prayerTimes/${locationId}/${prayerTimeTableId}/${currentYear}/${month}`;

    const result = await execute(endpoint, "GET");
    setPrayerTimesData(result || []);
  };

  const fetchMosqueInfo = async () => {
    if (!selectedOrgNumber) {
      console.error("selectedOrgNumber is null or undefined.");
      return;
    }

    try {
      const mosqueResult = await fetchMosqueData(
        `/api/v1/mosques/${selectedOrgNumber}`,
        "GET",
      );

      if (mosqueResult) {
        setAsrMethod(mosqueResult.asrMethod ?? null);
        if (mosqueResult.locationId) {
          setLocationId(mosqueResult.locationId);
        } else {
          console.error("locationId is missing in mosqueResult:", mosqueResult);
        }

        if (mosqueResult.prayerTimeTable) {
          setPrayerTimeTableId(mosqueResult.prayerTimeTable);
        } else {
          console.error(
            "prayerTimeTable is missing in mosqueResult:",
            mosqueResult,
          );
        }
      }
    } catch (error) {
      console.error("Failed to fetch mosque info:", error);
    }
  };

  const fetchCsvData = async () => {
    if (!locationId || !prayerTimeTableId) return;

    const url = `/api/v1/prayerTimes/${locationId}/${prayerTimeTableId}/${currentYear}/download-csv`;
    try {
      const result = await execute(url, "GET");
      setCsvData(result || []);
    } catch (error) {
      console.error("Failed to fetch CSV data", error);
    }
  };

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      const formData = new FormData();
      formData.append("file", file);

      const uploadUrl = `/api/v1/prayerTimes/${locationId}/${prayerTimeTableId}/${currentYear}/upload-csv`;

      try {
        await execute(uploadUrl, "POST", formData);
        console.log("File uploaded successfully");
        fetchPrayerTimes();
      } catch (error) {
        console.error("File upload failed", error);
      }
    }
  };

  useEffect(() => {
    if (selectedOrgNumber) {
      fetchMosqueInfo();
    }
  }, [selectedOrgNumber]);

  useEffect(() => {
    fetchPrayerTimes();
    fetchCsvData();
  }, [currentMonth, currentYear, locationId, prayerTimeTableId]);

  useEffect(() => {
    fetchCsvData();
  }, [locationId, prayerTimeTableId, currentYear]);

  const handleMonthChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(parseInt(event.target.value));
    setCurrentMonth(newMonth);
  };

  const handleYearChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setCurrentYear(parseInt(event.target.value));
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const today = new Date();
  const isToday = (date: string) => {
    const d = new Date(date);
    return (
      d.getFullYear() === today.getFullYear() &&
      d.getMonth() === today.getMonth() &&
      d.getDate() === today.getDate()
    );
  };

  const formatDate = (date: string) => {
    const d = new Date(date);
    return `${d.getDate()}. ${MONTHS_SHORT[d.getMonth()]}`;
  };

  const time = (value: string) => <span className={ui.num}>{value}</span>;

  const renderPrayerTimes = () => (
    <Table
      caption={`Bønnetider for ${getMonthName(currentMonth)} ${currentYear}`}
      rows={prayerTimesData}
      rowKey={(row) => row.date}
      rowState={(row) => (isToday(row.date) ? "on" : undefined)}
      empty={
        <EmptyState icon="clock" title="Ingen bønnetider">
          Det finnes ingen bønnetider for {getMonthName(currentMonth)}{" "}
          {currentYear}.
        </EmptyState>
      }
      columns={[
        {
          key: "date",
          header: "Dato",
          render: (row) => <b>{formatDate(row.date)}</b>,
        },
        { key: "fajr", header: "Fajr", render: (row) => time(row.fajr) },
        {
          key: "sunrise",
          header: "Soloppgang",
          render: (row) => time(row.fajr_endtime),
        },
        { key: "duhr", header: "Dhuhr", render: (row) => time(row.duhr) },
        { key: "asr", header: "Asr", render: (row) => time(row.asr) },
        {
          key: "asr1",
          header: "Asr 1x",
          render: (row) => time(row.asr_1x_shadow),
        },
        {
          key: "asr2",
          header: "Asr 2x",
          render: (row) => time(row.asr_2x_shadow),
        },
        {
          key: "maghrib",
          header: "Maghrib",
          render: (row) => time(row.maghrib),
        },
        { key: "isha", header: "Isha", render: (row) => time(row.isha) },
      ]}
    />
  );

  const renderError = () =>
    error && (
      <EmptyState icon="clock" title="Ingen bønnetider funnet">
        Fant ingen bønnetider for {getMonthName(currentMonth)} {currentYear}.
      </EmptyState>
    );

  return (
    <>
      <PageHeader
        crumb={mosqueName || selectedOrgName || undefined}
        title="Bønnetider"
        subtitle="Tidene kommer fra bønnetabellen moskeen bruker. De kan ikke endres her."
        actions={
          <CSVLink
            data={csvData}
            filename={`prayer_times_${locationId}_${prayerTimeTableId}_${currentYear}.csv`}
            className={`${ui.btn} ${ui.secondary}`}
          >
            <Icon name="download" size={16} strokeWidth={2} />
            Eksporter CSV for {currentYear}
          </CSVLink>
        }
      />
      <div className={styles.toolbar}>
        {asrMethod && (
          <Badge tone="gold">Asr: {ASR_LABELS[asrMethod] ?? asrMethod}</Badge>
        )}
        <span className={styles.pickers}>
          <Select
            aria-label="Måned"
            className={styles.month}
            value={currentMonth.getMonth()}
            onChange={handleMonthChange}
            options={MONTHS_LONG.map((name, index) => ({
              value: index,
              label: name.charAt(0).toUpperCase() + name.slice(1),
            }))}
          />
          <Select
            aria-label="År"
            className={`${ui.num} ${styles.year}`}
            value={currentYear}
            onChange={handleYearChange}
            options={Array.from({ length: 7 }, (_, index) => {
              const year = new Date().getFullYear() + index;
              return { value: year, label: year };
            })}
          />
        </span>
      </div>
      <Card>
        {isLoading ? (
          <PrayerTimesSkeleton />
        ) : error ? (
          renderError()
        ) : (
          renderPrayerTimes()
        )}
      </Card>
    </>
  );
};

const ASR_LABELS: Record<string, string> = {
  NONE: "Ingen",
  MIXED: "Mixed",
  ONE_X: "1x",
  TWO_X: "2x",
};

const MONTHS_SHORT = [
  "jan",
  "feb",
  "mar",
  "apr",
  "mai",
  "jun",
  "jul",
  "aug",
  "sep",
  "okt",
  "nov",
  "des",
];

const MONTHS_LONG = [
  "januar",
  "februar",
  "mars",
  "april",
  "mai",
  "juni",
  "juli",
  "august",
  "september",
  "oktober",
  "november",
  "desember",
];

export default PrayerTimesPage;
